import createAnonProfile from '@/services/internal/auth/createAnonProfile';
import fetchAuthCredential from '@/services/internal/auth/fetchAuthCredential';
import heartbeat from '@/services/internal/auth/heartbeat';
import loginUserWithUserName from '@/services/internal/auth/loginUserWithUserName';
import loginUserWithPhone from '@/services/internal/auth/loginUserWithPhone';
import logout from '@/services/internal/auth/logout';

type QueueItem = {
  type: string;
  execute: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
};

type OAuthResponse = {
  success: boolean;
  error?: {
    responseObject: {
      localizedDescription: string;
    };
  };
};

class AuthenticationQueue {
  private queue: QueueItem[] = [];
  private isProcessing: boolean = false;
  private pendingRequests: Map<string, Promise<any>> = new Map();

  private async processQueue(): Promise<void> {
    if (this.isProcessing || !this.queue.length) return;

    const request = this.queue[0];
    this.isProcessing = true;

    try {
      const result = await request.execute();
      request.resolve(result);
    } catch (error) {
      request.reject(error);
    } finally {
      this.pendingRequests.delete(request.type);
      this.queue.shift();
      this.isProcessing = false;
      this.processQueue();
    }
  }

  private addToQueue(item: QueueItem): Promise<any> {
    if (typeof window === 'undefined') {
      // Do not queue requests on the backend as this may lead to identity leak
      throw new Error('Invalid call');
    }

    const pending = this.pendingRequests.get(item.type);
    if (pending) {
      return pending;
    }

    const request = new Promise((resolve, reject) => {
      item.resolve = resolve;
      item.reject = reject;
    });

    this.pendingRequests.set(item.type, request);
    this.queue.push(item);
    this.processQueue();

    return request;
  }

  createAnonProfile(): Promise<Response> {
    return this.addToQueue({
      type: 'createAnonProfile',
      execute: createAnonProfile,
    } as QueueItem);
  }

  fetchAuthCredential(): Promise<string> {
    return this.addToQueue({
      type: 'fetchAuthCredential',
      execute: fetchAuthCredential,
    } as QueueItem);
  }

  refreshToken(): Promise<Response> {
    return this.addToQueue({
      type: 'refreshToken',
      execute: heartbeat,
    } as QueueItem);
  }

  loginUserWithUserName(username: string, password: string): Promise<OAuthResponse> {
    return this.addToQueue({
      type: 'loginUserWithUserName',
      execute: async () => loginUserWithUserName(username, password),
    } as QueueItem);
  }

  loginUserWithPhone(phone: string, code: string, gid: string): Promise<OAuthResponse> {
    return this.addToQueue({
      type: 'loginUserWithPhone',
      execute: async () => loginUserWithPhone(phone, code, gid),
    } as QueueItem);
  }

  logout(): Promise<Response> {
    return this.addToQueue({
      type: 'logout',
      execute: logout,
    } as QueueItem);
  }

  logoutToSC(): Promise<Response> {
    return this.addToQueue({
      type: 'logout',
      execute: () => logout(true),
    } as QueueItem);
  }
}

const auth = new AuthenticationQueue();

export default auth;
